// Edge-safe number formatters for server-rendered surfaces (OG cards + SEO meta).
//
// Satori runs inside Edge runtime where `Intl.NumberFormat` is technically
// available but inconsistent across deploy targets, and there is NO user session
// — a crawler has no currency preference. So server money renders with the base
// `$` symbol and US grouping, in pure arithmetic, via this ONE formatter
// (decision §09 of the currency-localisation plan). The client lib's
// `useFormattedCurrency` cannot run here. No token name is printed.
//
// All formatters return ASCII strings — no thin spaces, no `−` minus.
//
// Note: `BigInt(...)` constructor instead of `100n` literals because
// `tsconfig.json` targets ES2017 (BigInt literals require ES2020+).

import { D6_SCALE, D9_SCALE } from '@/config/solana';

const D6: bigint = BigInt(D6_SCALE);
const D9_TO_D6: bigint = BigInt(D9_SCALE) / D6;
const HALF_D9_TO_D6: bigint = D9_TO_D6 / BigInt(2);
const ZERO: bigint = BigInt(0);
const ONE: bigint = BigInt(1);
const FIVE_THOUSAND: bigint = BigInt(5_000);
const TEN_THOUSAND: bigint = BigInt(10_000);

/** Insert thousands separators (commas) into a non-negative integer. */
function withThousands(intStr: string): string {
  return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function abs(n: number): number {
  return n < 0 ? -n : n;
}

function absBig(n: bigint): bigint {
  return n < ZERO ? -n : n;
}

/**
 * Format an abbreviated number — e.g. `8142` → `"8,142"`, `12_400_000` → `"12.4M"`.
 */
export function formatOgAbbrev(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const sign = value < 0 ? '-' : '';
  const n = abs(value);

  if (n < 10_000) return sign + withThousands(Math.round(n).toString());

  const scale = (divisor: number, suffix: string): string => {
    const v = n / divisor;
    const digits = v < 100 ? 1 : 0;
    const rounded = Math.round(v * 10 ** digits) / 10 ** digits;
    return sign + rounded.toFixed(digits) + suffix;
  };

  if (n < D6_SCALE) return scale(1_000, 'K');
  if (n < D9_SCALE) return scale(D6_SCALE, 'M');
  return scale(D9_SCALE, 'B');
}

export type OgUsdcMode = 'full' | 'compact' | 'price';

/** Large prize headlines abbreviate from $1K, using fixed-point arithmetic. */
export function formatOgPrizeD6(d6: bigint): string {
  const positive = absBig(d6);
  const thousand = BigInt(1000);
  const units: readonly (readonly [bigint, string])[] = [
    [thousand ** BigInt(3) * D6, 'B'],
    [thousand ** BigInt(2) * D6, 'M'],
    [thousand * D6, 'K'],
  ];
  for (const [unit, suffix] of units) {
    if (positive < unit) continue;
    const tenths = (positive * BigInt(10) + unit / BigInt(2)) / unit;
    const fraction = tenths % BigInt(10);
    const decimal = fraction === ZERO ? '' : `.${fraction}`;
    return `${d6 < ZERO ? '-' : ''}$${tenths / BigInt(10)}${decimal}${suffix}`;
  }
  return formatOgUsdcD6(d6).replace(/\.00$/, '');
}

/**
 * Format a D6-encoded USDC amount as a USD string.
 * - `full` (default): `$1,234.56`
 * - `compact`: `$1.2K` / `$12.4M` for amounts ≥ $10 000
 * - `price`: `$0.0312` — preserves significant sub-cent D6 digits
 * Always prefixes `$` (base currency; crawler has no locale).
 */
export function formatOgUsdcD6(d6: bigint | number, mode: OgUsdcMode = 'full'): string {
  const big: bigint = typeof d6 === 'bigint' ? d6 : BigInt(Math.trunc(d6));
  const sign = big < ZERO ? '-' : '';
  const positive = absBig(big);

  if (mode === 'compact') {
    const asFloat = Number(positive) / D6_SCALE;
    if (asFloat >= 10_000) return `${sign}$${formatOgAbbrev(asFloat)}`;
  }

  const dollars = positive / D6;
  const cents = positive % D6;
  if (mode === 'price') {
    const significantFraction = cents.toString().padStart(6, '0').replace(/0+$/, '');
    const fraction = significantFraction.padEnd(2, '0');
    return `${sign}$${withThousands(dollars.toString())}.${fraction}`;
  }
  const cents2 = Number((cents + FIVE_THOUSAND) / TEN_THOUSAND);
  let displayDollars = dollars;
  let displayCents = cents2;
  if (displayCents === 100) {
    displayDollars = displayDollars + ONE;
    displayCents = 0;
  }
  const centsStr = displayCents.toString().padStart(2, '0');
  return `${sign}$${withThousands(displayDollars.toString())}.${centsStr}`;
}

/** Format a D9 price; full mode rounds to cents, price mode preserves sub-cent digits. */
export function formatOgPriceD9(d9: bigint, mode: 'full' | 'price' = 'price'): string {
  // Truncate sub-micro digits before full-mode rounding to avoid rounding twice at half cents.
  if (mode === 'full') return formatOgUsdcD6(d9 / D9_TO_D6, 'full');
  const roundedD6 =
    d9 < ZERO ? -((-d9 + HALF_D9_TO_D6) / D9_TO_D6) : (d9 + HALF_D9_TO_D6) / D9_TO_D6;
  return formatOgUsdcD6(roundedD6, 'price');
}

/** Format a normalised probability (0..1) as a whole-percent string. */
export function formatOgPercent(value: number): string {
  if (!Number.isFinite(value)) return '0%';
  const clamped = value < 0 ? 0 : value > 1 ? 1 : value;
  return `${Math.round(clamped * 100).toString()}%`;
}

/** Format a non-negative percent magnitude for a 24h-change label. */
export function formatOgPercentMagnitude(magnitude: number): string {
  if (!Number.isFinite(magnitude)) return '0%';
  const m = abs(magnitude);
  return m >= 100 ? `${Math.round(m).toString()}%` : `${(Math.round(m * 10) / 10).toFixed(1)}%`;
}

const MONTH_NAMES_SHORT: readonly string[] = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** Format a Date as `28 Apr 2026, 14:32 UTC` for "As of …" timestamps. */
export function formatOgAsOf(date: Date): string {
  const day = date.getUTCDate();
  const month = MONTH_NAMES_SHORT[date.getUTCMonth()] ?? '';
  const year = date.getUTCFullYear();
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  return `${day.toString()} ${month} ${year.toString()}, ${hours}:${minutes} UTC`;
}

/** Spelled-out age for resolved market OG footers ("6 weeks", not "6w"). */
export function formatOgAgeLong(iso: string): string {
  const created = new Date(iso).getTime();
  if (!Number.isFinite(created)) return '0 days';
  const ms = Date.now() - created;
  if (ms <= 0) return '0 days';
  const days = Math.floor(ms / 86_400_000);
  if (days < 7) return days === 1 ? '1 day' : `${days.toString()} days`;
  const weeks = Math.floor(days / 7);
  if (weeks < 8) return weeks === 1 ? '1 week' : `${weeks.toString()} weeks`;
  const months = Math.floor(days / 30);
  if (months < 24) return months === 1 ? '1 month' : `${months.toString()} months`;
  const years = Math.floor(days / 365);
  return years === 1 ? '1 year' : `${years.toString()} years`;
}
